import React, { useState } from 'react';
import { supabase } from '../services/supabaseClient';
import { ORG_NAME } from '../constants';
import { Shield, AlertCircle } from 'lucide-react';
import { Button } from '../components/ui/button';

// Check if running in local development
const IS_LOCAL_DEV = import.meta.env.DEV &&
  (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');

const Login: React.FC = () => {
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [showDevLogin, setShowDevLogin] = useState(false);
  const [email, setEmail] = useState('test@test.com');
  const [password, setPassword] = useState('testtest123');

  const handleLogin = async () => {
    setLoading(true);
    setError(null);
    try {
      // This triggers the OAuth flow.
      // Configure 'azure' provider in Supabase Dashboard.
      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'azure',
        options: {
          scopes: 'email',
          redirectTo: window.location.origin
        }
      });
      if (error) throw error;
    } catch (e: any) {
      setError(e.message || "Failed to initiate login");
      setLoading(false);
    }
  };

  const handleDevLogin = async () => {
    setLoading(true);
    setError(null);
    try {
      const { error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });
      if (error) throw error;
    } catch (e: any) {
      setError(e.message || "Failed to login");
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-zinc-50 dark:bg-zinc-950 flex flex-col items-center justify-center p-4">
      <div className="max-w-md w-full bg-white dark:bg-zinc-900 rounded-2xl shadow-sm border border-zinc-200 dark:border-zinc-800 p-8 text-center">
        <div className="w-16 h-16 bg-blue-600 rounded-xl flex items-center justify-center mx-auto mb-6 shadow-sm shadow-blue-900/10">
          <Shield className="h-8 w-8 text-white" />
        </div>
        
        <h1 className="text-2xl font-bold text-zinc-900 dark:text-white mb-2">{ORG_NAME} OS</h1>
        <p className="text-zinc-500 dark:text-zinc-400 mb-8">Editorial Workflow & AI Automation Platform</p>
        
        {error && (
          <div className="mb-6 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg flex items-center text-left">
            <AlertCircle className="h-5 w-5 text-red-600 dark:text-red-400 mr-3 flex-shrink-0" />
            <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
          </div>
        )}

        <Button
          onClick={handleLogin}
          disabled={loading}
          className="w-full"
          size="lg"
        >
          {loading ? (
            <span className="animate-pulse">Connecting...</span>
          ) : (
            <>
              <svg className="h-5 w-5 mr-3" viewBox="0 0 23 23" fill="currentColor">
                <path fill="#f35325" d="M1 1h10v10H1z"/>
                <path fill="#81bc06" d="M12 1h10v10H12z"/>
                <path fill="#05a6f0" d="M1 12h10v10H1z"/>
                <path fill="#ffba08" d="M12 12h10v10H12z"/>
              </svg>
              Sign in with Microsoft
            </>
          )}
        </Button>

        {/* Dev-only email/password login for local testing */}
        {IS_LOCAL_DEV && (
          <div className="mt-6 pt-6 border-t border-zinc-200 dark:border-zinc-700">
            {!showDevLogin ? (
              <button
                onClick={() => setShowDevLogin(true)}
                className="text-xs text-zinc-400 hover:text-zinc-600 dark:hover:text-zinc-300"
              >
                Dev Login (local only)
              </button>
            ) : (
              <div className="space-y-3">
                <p className="text-xs text-amber-600 dark:text-amber-400 font-medium">
                  Local Development Login
                </p>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Email"
                  className="w-full px-3 py-2 text-sm border border-zinc-300 dark:border-zinc-600 rounded-lg bg-white dark:bg-zinc-800 text-zinc-900 dark:text-white"
                />
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Password"
                  className="w-full px-3 py-2 text-sm border border-zinc-300 dark:border-zinc-600 rounded-lg bg-white dark:bg-zinc-800 text-zinc-900 dark:text-white"
                />
                <Button
                  onClick={handleDevLogin}
                  disabled={loading}
                  variant="outline"
                  className="w-full"
                  size="sm"
                >
                  {loading ? 'Logging in...' : 'Dev Login'}
                </Button>
              </div>
            )}
          </div>
        )}

        <p className="mt-6 text-xs text-zinc-400 dark:text-zinc-500">
          Authorized personnel only. All activity is logged.
        </p>
      </div>
    </div>
  );
};

export default Login;